import { useEffect, useState } from 'react'
import api from '../api/client'
import { successModal, errorModal, confirmModal } from '../components/ui'
import Loader from '../components/Loader'

const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'
const lbl = 'block text-xs font-medium text-gray-600 mb-1'

// ─────────────── Matriz de permissões (módulos × ações) ───────────────
function Matriz({ modulos, acoes, value, onChange, disabled }) {
  const perms = value || {}
  const set = (m, a, v) => onChange({ ...perms, [m]: { ...(perms[m] || {}), [a]: v } })
  const toggleModulo = (m, v) => onChange({ ...perms, [m]: Object.fromEntries(acoes.map((a) => [a, v])) })
  return (
    <div className="overflow-x-auto max-h-[55vh] overflow-y-auto border border-gray-100 rounded-xl">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-gray-50 z-10">
          <tr className="text-left text-xs text-gray-500">
            <th className="py-2 px-3">Módulo</th>
            {acoes.map((a) => <th key={a} className="py-2 px-2 text-center capitalize">{a}</th>)}
            <th className="py-2 px-2 text-center">Tudo</th>
          </tr>
        </thead>
        <tbody>
          {modulos.map((m) => {
            const pm = perms[m] || {}
            const all = acoes.every((a) => pm[a])
            return (
              <tr key={m} className="border-t border-gray-50">
                <td className="py-1.5 px-3 font-medium text-gray-700">{m}</td>
                {acoes.map((a) => (
                  <td key={a} className="py-1.5 px-2 text-center">
                    <input type="checkbox" disabled={disabled} checked={!!pm[a]} onChange={(e) => set(m, a, e.target.checked)} className="w-4 h-4 accent-accent-500" />
                  </td>
                ))}
                <td className="py-1.5 px-2 text-center">
                  <input type="checkbox" disabled={disabled} checked={all} onChange={(e) => toggleModulo(m, e.target.checked)} className="w-4 h-4 accent-emerald-500" />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────── Aba Funcionários ───────────────
function AbaFuncionarios({ meta, departamentos, recarregarDeps }) {
  const vazio = { nome: '', cpf: '', data_nascimento: '', email_corporativo: '', celular: '', whatsapp: '',
    cargo: '', departamento_id: '', supervisor_id: '', status: 'ativo', observacoes: '', perfil: 'operador',
    dois_fatores: false, foto_url: '', login: '', senha: '' }
  const [lista, setLista] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtros, setFiltros] = useState({ q: '', departamento_id: '', perfil: '', status: '' })
  const [edit, setEdit] = useState(null)

  async function carregar() {
    setLoading(true)
    try {
      const p = new URLSearchParams(Object.entries(filtros).filter(([, v]) => v)).toString()
      const { data } = await api.get('/funcionarios' + (p ? `?${p}` : ''))
      setLista(data.data || [])
    } catch { setLista([]) } finally { setLoading(false) }
  }
  useEffect(() => { carregar() }, [])   // eslint-disable-line

  const set = (k) => (e) => setEdit({ ...edit, [k]: e.target.value })
  async function salvar() {
    if (!edit.nome) { errorModal('Informe o nome.'); return }
    try {
      const body = { ...edit, departamento_id: edit.departamento_id ? Number(edit.departamento_id) : null,
        supervisor_id: edit.supervisor_id ? Number(edit.supervisor_id) : null }
      if (edit.id) await api.put(`/funcionarios/${edit.id}`, body)
      else await api.post('/funcionarios', body)
      successModal(edit.id ? 'Funcionário atualizado!' : 'Funcionário cadastrado!')
      setEdit(null); carregar()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao salvar') }
  }
  async function acao(f, tipo) {
    const txt = { bloquear: 'bloquear', desbloquear: 'desbloquear', excluir: 'excluir' }[tipo]
    if (!(await confirmModal(`Confirmar ${txt} o funcionário ${f.nome}?`))) return
    try {
      if (tipo === 'excluir') await api.delete(`/funcionarios/${f.id}`)
      else await api.post(`/funcionarios/${f.id}/${tipo}`)
      successModal('Feito.'); carregar()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro') }
  }
  async function resetSenha(f) {
    if (!(await confirmModal(`Redefinir a senha de ${f.nome}? Uma senha temporária será gerada.`))) return
    try { const { data } = await api.post(`/funcionarios/${f.id}/redefinir-senha`)
      successModal(`Senha temporária: ${data.senha_temporaria}\n${data.aviso}`, { title: 'Senha redefinida' })
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro') }
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]"><label className={lbl}>Buscar (nome/CPF/e-mail/cargo)</label>
          <input value={filtros.q} onChange={(e) => setFiltros({ ...filtros, q: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && carregar()} className={inp} /></div>
        <div><label className={lbl}>Departamento</label>
          <select value={filtros.departamento_id} onChange={(e) => setFiltros({ ...filtros, departamento_id: e.target.value })} className={inp + ' bg-white'}>
            <option value="">Todos</option>{departamentos.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}</select></div>
        <div><label className={lbl}>Perfil</label>
          <select value={filtros.perfil} onChange={(e) => setFiltros({ ...filtros, perfil: e.target.value })} className={inp + ' bg-white'}>
            <option value="">Todos</option>{(meta.perfis || []).map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
        <div><label className={lbl}>Status</label>
          <select value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })} className={inp + ' bg-white'}>
            <option value="">Todos</option>{(meta.status || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
        <button onClick={carregar} className="rounded-xl border border-accent-300 text-accent-600 px-4 py-2 text-sm font-semibold hover:bg-accent-50">Filtrar</button>
        <button onClick={() => setEdit({ ...vazio })} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-semibold shadow-md hover:brightness-95">+ Novo funcionário</button>
      </div>

      <div className="bg-white rounded-2xl p-2 shadow-sm border border-gray-100 overflow-x-auto">
        {loading ? <div className="py-12 flex justify-center"><Loader text="Carregando..." /></div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-400 border-b border-gray-100">
              <th className="py-2 px-3">Funcionário</th><th>Departamento</th><th>Cargo</th><th>Perfil</th><th>Último acesso</th><th>Status</th><th className="text-right px-3">Ações</th></tr></thead>
            <tbody>
              {lista.map((f) => (
                <tr key={f.id} className="border-b border-gray-50 hover:bg-primary-50/30">
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      {f.foto_url ? <img src={f.foto_url} alt="" className="w-8 h-8 rounded-full object-cover" /> : <span className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500">{(f.nome || '?')[0]}</span>}
                      <div><div className="font-medium text-gray-800">{f.nome}</div><div className="text-[11px] text-gray-400">{f.email_corporativo || f.login || ''}</div></div>
                    </div>
                  </td>
                  <td className="text-gray-600">{f.departamento_nome || '—'}</td>
                  <td className="text-gray-600">{f.cargo || '—'}</td>
                  <td><span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">{f.perfil}</span></td>
                  <td className="text-gray-500 text-xs">{f.ultimo_acesso ? new Date(f.ultimo_acesso).toLocaleString('pt-BR') : '—'}</td>
                  <td><span className={`text-xs px-2 py-0.5 rounded-full ${f.status === 'ativo' ? 'bg-emerald-50 text-emerald-600' : f.status === 'bloqueado' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'}`}>{f.status}</span></td>
                  <td className="px-3">
                    <div className="flex items-center justify-end gap-2 text-xs">
                      <button onClick={() => setEdit({ ...vazio, ...f, senha: '' })} className="text-accent-600 hover:underline">Editar</button>
                      {f.status === 'bloqueado'
                        ? <button onClick={() => acao(f, 'desbloquear')} className="text-emerald-600 hover:underline">Desbloq.</button>
                        : <button onClick={() => acao(f, 'bloquear')} className="text-amber-600 hover:underline">Bloquear</button>}
                      <button onClick={() => resetSenha(f)} className="text-blue-600 hover:underline">Senha</button>
                      <button onClick={() => acao(f, 'excluir')} className="text-red-500 hover:underline">Excluir</button>
                    </div>
                  </td>
                </tr>
              ))}
              {lista.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-gray-400">Nenhum funcionário.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[92vh] overflow-y-auto tc-scroll">
            <h2 className="text-lg font-heading font-bold text-gray-900 mb-4">{edit.id ? 'Editar' : 'Novo'} funcionário</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Dados do funcionário</p>
                <div><label className={lbl}>Foto (URL)</label><input value={edit.foto_url} onChange={set('foto_url')} className={inp} /></div>
                <div><label className={lbl}>Nome completo *</label><input value={edit.nome} onChange={set('nome')} className={inp} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>CPF</label><input value={edit.cpf} onChange={set('cpf')} className={inp} /></div>
                  <div><label className={lbl}>Nascimento</label><input value={edit.data_nascimento} onChange={set('data_nascimento')} placeholder="DD/MM/AAAA" className={inp} /></div>
                </div>
                <div><label className={lbl}>E-mail corporativo</label><input value={edit.email_corporativo} onChange={set('email_corporativo')} className={inp} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Celular</label><input value={edit.celular} onChange={set('celular')} className={inp} /></div>
                  <div><label className={lbl}>WhatsApp</label><input value={edit.whatsapp} onChange={set('whatsapp')} className={inp} /></div>
                </div>
                <div><label className={lbl}>Cargo</label><input value={edit.cargo} onChange={set('cargo')} className={inp} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Departamento</label>
                    <select value={edit.departamento_id || ''} onChange={set('departamento_id')} className={inp + ' bg-white'}>
                      <option value="">—</option>{departamentos.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}</select></div>
                  <div><label className={lbl}>Supervisor</label>
                    <select value={edit.supervisor_id || ''} onChange={set('supervisor_id')} className={inp + ' bg-white'}>
                      <option value="">—</option>{lista.filter((x) => x.id !== edit.id).map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}</select></div>
                </div>
                <div><label className={lbl}>Status</label>
                  <select value={edit.status} onChange={set('status')} className={inp + ' bg-white'}>
                    {(meta.status || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
                <div><label className={lbl}>Observações</label><textarea rows={2} value={edit.observacoes} onChange={set('observacoes')} className={inp} /></div>
              </div>
              <div className="space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Acesso à plataforma</p>
                <div><label className={lbl}>Perfil</label>
                  <select value={edit.perfil} onChange={set('perfil')} className={inp + ' bg-white'}>
                    {(meta.perfis || []).map((p) => <option key={p} value={p}>{p}</option>)}</select>
                  <p className="text-[11px] text-gray-400 mt-1">master = acesso total. Demais herdam do departamento (ajustável em Perfis de Acesso).</p></div>
                <div><label className={lbl}>Login (e-mail)</label><input value={edit.login} onChange={set('login')} placeholder={edit.email_corporativo || 'email de acesso'} className={inp} /></div>
                <div><label className={lbl}>{edit.id ? 'Nova senha (deixe em branco p/ manter)' : 'Senha'}</label><input type="text" value={edit.senha} onChange={set('senha')} className={inp} /></div>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pt-1">
                  <input type="checkbox" checked={!!edit.dois_fatores} onChange={(e) => setEdit({ ...edit, dois_fatores: e.target.checked })} className="w-4 h-4 accent-accent-500" /> Exigir dois fatores (2FA)
                </label>
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Ao definir login+senha, cria-se o acesso (perfil admin). A senha exige troca no 1º acesso. Bloqueio automático após 5 tentativas inválidas.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button onClick={() => setEdit(null)} className="px-4 py-2 text-gray-500 hover:text-gray-700">Cancelar</button>
              <button onClick={salvar} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 font-semibold shadow-md hover:brightness-95">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────── Aba Departamentos ───────────────
function AbaDepartamentos({ meta, departamentos, recarregar }) {
  const [edit, setEdit] = useState(null)
  const novo = () => setEdit({ nome: '', descricao: '', template: '', ativo: true, permissoes: {} })
  async function salvar() {
    if (!edit.nome) { errorModal('Informe o nome do departamento.'); return }
    try {
      if (edit.id) await api.put(`/funcionarios/departamentos/${edit.id}`, edit)
      else await api.post('/funcionarios/departamentos', edit)
      successModal('Departamento salvo!'); setEdit(null); recarregar()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao salvar') }
  }
  async function excluir(d) {
    if (!(await confirmModal(`Excluir o departamento ${d.nome}?`))) return
    try { await api.delete(`/funcionarios/departamentos/${d.id}`); successModal('Excluído.'); recarregar() }
    catch (e) { errorModal(e?.response?.data?.detail || 'Erro') }
  }
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><button onClick={novo} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-semibold shadow-md hover:brightness-95">+ Novo departamento</button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {departamentos.map((d) => (
          <div key={d.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between"><span className="font-semibold text-gray-900">{d.nome}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${d.ativo ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>{d.ativo ? 'ativo' : 'inativo'}</span></div>
            <p className="text-xs text-gray-500 mt-1 min-h-[32px]">{d.descricao || '—'}</p>
            <div className="flex gap-3 mt-2 text-xs">
              <button onClick={() => setEdit({ ...d, template: '' })} className="text-accent-600 hover:underline">Editar / Permissões</button>
              <button onClick={() => excluir(d)} className="text-red-500 hover:underline">Excluir</button>
            </div>
          </div>
        ))}
        {departamentos.length === 0 && <p className="text-sm text-gray-400">Nenhum departamento. Crie o primeiro (ex.: Financeiro, Suporte, Marketing).</p>}
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[92vh] overflow-y-auto tc-scroll space-y-3">
            <h2 className="text-lg font-heading font-bold text-gray-900">{edit.id ? 'Editar' : 'Novo'} departamento</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={lbl}>Nome *</label><input value={edit.nome} onChange={(e) => setEdit({ ...edit, nome: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>Aplicar template de permissões</label>
                <select value={edit.template || ''} onChange={(e) => setEdit({ ...edit, template: e.target.value })} className={inp + ' bg-white'}>
                  <option value="">— manter atual —</option>{(meta.templates || []).map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
            </div>
            <div><label className={lbl}>Descrição</label><input value={edit.descricao || ''} onChange={(e) => setEdit({ ...edit, descricao: e.target.value })} className={inp} /></div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={!!edit.ativo} onChange={(e) => setEdit({ ...edit, ativo: e.target.checked })} className="w-4 h-4 accent-accent-500" /> Ativo</label>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Matriz de permissões {edit.template && <span className="text-accent-600 normal-case">(o template será aplicado ao salvar)</span>}</p>
              <Matriz modulos={meta.modulos || []} acoes={meta.acoes || []} value={edit.permissoes || {}} onChange={(v) => setEdit({ ...edit, permissoes: v, template: '' })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEdit(null)} className="px-4 py-2 text-gray-500 hover:text-gray-700">Cancelar</button>
              <button onClick={salvar} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 font-semibold shadow-md hover:brightness-95">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────── Aba Perfis de Acesso (permissões por funcionário) ───────────────
function AbaPerfis({ meta, departamentos }) {
  const [funcs, setFuncs] = useState([])
  const [selId, setSelId] = useState('')
  const [detalhe, setDetalhe] = useState(null)
  const [custom, setCustom] = useState(false)
  const [perms, setPerms] = useState({})

  useEffect(() => { api.get('/funcionarios').then((r) => setFuncs(r.data.data || [])).catch(() => {}) }, [])
  async function abrir(id) {
    setSelId(id); setDetalhe(null)
    if (!id) return
    try { const { data } = await api.get(`/funcionarios/${id}`)
      setDetalhe(data); setCustom(!!data.permissoes_custom); setPerms(data.permissoes_custom || data.permissoes_efetivas || {}) }
    catch (e) { errorModal(e?.response?.data?.detail || 'Erro') }
  }
  async function salvar() {
    try { await api.put(`/funcionarios/${selId}/permissoes`, { permissoes_custom: custom ? perms : null })
      successModal('Permissões salvas!'); abrir(selId) }
    catch (e) { errorModal(e?.response?.data?.detail || 'Erro') }
  }
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-end gap-3 flex-wrap">
        <div className="min-w-[260px]"><label className={lbl}>Funcionário</label>
          <select value={selId} onChange={(e) => abrir(e.target.value)} className={inp + ' bg-white'}>
            <option value="">Selecione…</option>
            {funcs.map((f) => <option key={f.id} value={f.id}>{f.nome} — {f.departamento_nome || 's/ depto'} ({f.perfil})</option>)}</select></div>
        {detalhe && (
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={custom} onChange={(e) => { setCustom(e.target.checked); if (!e.target.checked) setPerms(detalhe.permissoes_efetivas || {}) }} className="w-4 h-4 accent-accent-500" />
            Usar permissões PRÓPRIAS (sobrepõe o departamento)
          </label>
        )}
      </div>
      {detalhe && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-3">
          {detalhe.perfil === 'master'
            ? <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">Este funcionário é <b>master</b> — acesso total a todos os módulos.</p>
            : <>
                <p className="text-xs text-gray-500">{custom ? 'Editando permissões próprias deste funcionário.' : 'Exibindo as permissões herdadas do departamento (marque acima para personalizar).'}</p>
                <Matriz modulos={meta.modulos || []} acoes={meta.acoes || []} value={perms} onChange={setPerms} disabled={!custom} />
                <div className="flex justify-end"><button onClick={salvar} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 text-sm font-semibold shadow-md hover:brightness-95">Salvar permissões</button></div>
              </>}
        </div>
      )}
    </div>
  )
}

// ─────────────── Aba Logs ───────────────
function AbaLogs() {
  const [rows, setRows] = useState([]); const [q, setQ] = useState('')
  async function carregar() { try { const { data } = await api.get('/funcionarios/logs/lista' + (q ? `?q=${encodeURIComponent(q)}` : '')); setRows(data.data || []) } catch { setRows([]) } }
  useEffect(() => { carregar() }, [])   // eslint-disable-line
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-3">
        <div className="flex-1"><label className={lbl}>Buscar nos logs (usuário/ação/detalhe)</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && carregar()} className={inp} /></div>
        <button onClick={carregar} className="rounded-xl border border-accent-300 text-accent-600 px-4 py-2 text-sm font-semibold hover:bg-accent-50">Buscar</button>
      </div>
      <div className="bg-white rounded-2xl p-2 shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-400 border-b border-gray-100">
            <th className="py-2 px-3">Data</th><th>Usuário</th><th>Ação</th><th>Entidade</th><th>Detalhe</th><th>IP</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-gray-50">
                <td className="py-2 px-3 text-gray-500 whitespace-nowrap">{r.data ? new Date(r.data).toLocaleString('pt-BR') : ''}</td>
                <td className="text-gray-700">{r.usuario || '—'}</td>
                <td><span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{r.acao}</span></td>
                <td className="text-gray-500">{r.entidade || '—'}{r.entidade_id ? ` #${r.entidade_id}` : ''}</td>
                <td className="text-gray-600 max-w-md truncate" title={r.detalhe}>{r.detalhe || '—'}</td>
                <td className="text-gray-400 text-xs">{r.ip || '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-gray-400">Nenhum log.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function Funcionarios() {
  const [tab, setTab] = useState('funcionarios')
  const [meta, setMeta] = useState(null)
  const [departamentos, setDepartamentos] = useState([])

  async function carregarDeps() { try { const { data } = await api.get('/funcionarios/departamentos'); setDepartamentos(data.data || []) } catch {} }
  useEffect(() => {
    api.get('/funcionarios/meta').then((r) => setMeta(r.data)).catch(() => setMeta({ modulos: [], acoes: [], perfis: [], status: [], templates: [] }))
    carregarDeps()
  }, [])

  if (!meta) return <div className="flex justify-center py-20"><Loader text="Carregando gestão de funcionários..." /></div>

  return (
    <div className="max-w-6xl space-y-5">
      <div>
        <h1 className="text-2xl font-heading font-bold text-gray-900">Gestão de Funcionários</h1>
        <p className="text-gray-500 text-sm mt-1">Funcionários, departamentos e permissões internas da plataforma (Administrador Master).</p>
      </div>
      <div className="flex gap-2 border-b border-gray-200 flex-wrap">
        {[['funcionarios', 'Funcionários'], ['departamentos', 'Departamentos'], ['perfis', 'Perfis de Acesso'], ['logs', 'Logs de Auditoria']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? 'border-accent-500 text-accent-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>
      {tab === 'funcionarios' && <AbaFuncionarios meta={meta} departamentos={departamentos} recarregarDeps={carregarDeps} />}
      {tab === 'departamentos' && <AbaDepartamentos meta={meta} departamentos={departamentos} recarregar={carregarDeps} />}
      {tab === 'perfis' && <AbaPerfis meta={meta} departamentos={departamentos} />}
      {tab === 'logs' && <AbaLogs />}
    </div>
  )
}
